import { create } from 'zustand';

import { toPlayerMessage } from '@/shared/api';
import { isFresh, singleFlight } from '@/shared/lib';

import {
  fetchCurrentDraws,
  fetchMonthlyDraw,
  fetchSeasonFinal,
} from '../api/draws.api';

import type { CurrentDraws, MonthlyDraw, SeasonFinalDraw } from './types';

/**
 * Розыгрыши глазами игрока.
 *
 * `loadedAt` тут не оптимизация, а необходимость: `null` — валидный ответ
 * («розыгрыш ещё не проводился»), и по самому полю нельзя отличить его
 * от «мы ещё не спрашивали». Без отдельной отметки виджет либо вечно
 * показывал бы скелет, либо запрашивал заново на каждый рендер.
 */
interface DrawsState {
  /** Что разыгрывается сейчас — для блоков «Призы месяца» и «Финальный приз». */
  current: CurrentDraws | null;
  currentIsLoading: boolean;
  currentError: string | null;
  currentLoadedAt: number | null;

  /** Результат уже проведённого розыгрыша — для итогов игрока. */
  monthly: MonthlyDraw | null;
  monthlyIsLoading: boolean;
  monthlyError: string | null;
  monthlyLoadedAt: number | null;

  seasonFinal: SeasonFinalDraw | null;
  seasonFinalIsLoading: boolean;
  seasonFinalError: string | null;
  seasonFinalLoadedAt: number | null;

  loadCurrent: (options?: { force?: boolean }) => Promise<void>;
  loadMonthly: (options?: { force?: boolean }) => Promise<void>;
  loadSeasonFinal: (options?: { force?: boolean }) => Promise<void>;
  reset: () => void;
}

export const useDrawsStore = create<DrawsState>()((set, get) => ({
  current: null,
  currentIsLoading: false,
  currentError: null,
  currentLoadedAt: null,

  monthly: null,
  monthlyIsLoading: false,
  monthlyError: null,
  monthlyLoadedAt: null,

  seasonFinal: null,
  seasonFinalIsLoading: false,
  seasonFinalError: null,
  seasonFinalLoadedAt: null,

  loadCurrent: singleFlight(
    async (options?: { force?: boolean }): Promise<void> => {
      if (!options?.force && isFresh(get().currentLoadedAt)) return;

      set({ currentIsLoading: true, currentError: null });
      try {
        set({
          current: await fetchCurrentDraws(),
          currentLoadedAt: Date.now(),
        });
      } catch (error) {
        set({ currentError: toPlayerMessage(error) });
      } finally {
        set({ currentIsLoading: false });
      }
    },
  ),

  loadMonthly: singleFlight(
    async (options?: { force?: boolean }): Promise<void> => {
      if (!options?.force && isFresh(get().monthlyLoadedAt)) return;

      set({ monthlyIsLoading: true, monthlyError: null });
      try {
        set({ monthly: await fetchMonthlyDraw(), monthlyLoadedAt: Date.now() });
      } catch (error) {
        set({ monthlyError: toPlayerMessage(error) });
      } finally {
        set({ monthlyIsLoading: false });
      }
    },
  ),

  loadSeasonFinal: singleFlight(
    async (options?: { force?: boolean }): Promise<void> => {
      if (!options?.force && isFresh(get().seasonFinalLoadedAt)) return;

      set({ seasonFinalIsLoading: true, seasonFinalError: null });
      try {
        set({
          seasonFinal: await fetchSeasonFinal(),
          seasonFinalLoadedAt: Date.now(),
        });
      } catch (error) {
        set({ seasonFinalError: toPlayerMessage(error) });
      } finally {
        set({ seasonFinalIsLoading: false });
      }
    },
  ),

  reset: () => {
    set({
      current: null,
      currentError: null,
      currentLoadedAt: null,
      monthly: null,
      monthlyError: null,
      monthlyLoadedAt: null,
      seasonFinal: null,
      seasonFinalError: null,
      seasonFinalLoadedAt: null,
    });
  },
}));
